import React from 'react';
import { useNavigate } from 'react-router-dom';
import { PageLayout } from '@/shared/layout';
import { Button, Filter, Search, KPICard, Card, Badge, Table, Avatar } from '@/components/ui';
import { useConstructionNcrs } from './hooks/useNcr';
import { useAuth } from '@/core/auth/AuthContext';
import { AuthButton } from '@/core/permissions/AuthButton';
import type { NcrStatus, NcrSeverity } from './types/construction-ncr.types';

const statusColors: Record<NcrStatus, string> = {
  draft: 'bg-slate-100 text-slate-600',
  open: 'bg-red-100 text-red-600',
  under_review: 'bg-blue-100 text-blue-600',
  action_required: 'bg-amber-100 text-amber-600',
  rework_in_progress: 'bg-orange-100 text-orange-600',
  pending_verification: 'bg-indigo-100 text-indigo-600',
  verified: 'bg-emerald-100 text-emerald-600',
  closed: 'bg-slate-900 text-white',
  rejected: 'bg-slate-200 text-slate-500',
  cancelled: 'bg-slate-200 text-slate-400',
};

const statusLabels: Record<NcrStatus, string> = {
  draft: 'Bản thảo',
  open: 'Mới mở (Open)',
  under_review: 'Đang soát xét',
  action_required: 'Yêu cầu xử lý',
  rework_in_progress: 'Đang sửa lỗi (Rework)',
  pending_verification: 'Chờ xác minh',
  verified: 'Đã xác minh',
  closed: 'Đã đóng',
  rejected: 'Từ chối',
  cancelled: 'Đã hủy',
};

const severityColors: Record<NcrSeverity, string> = {
  low: 'bg-slate-100 text-slate-600',
  medium: 'bg-blue-100 text-blue-600',
  high: 'bg-orange-100 text-orange-600',
  critical: 'bg-red-600 text-white animate-pulse',
};

export default function NcrListPage() {
  const { profile } = useAuth();
  const companyId = profile?.company_id || '';
  const navigate = useNavigate();

  const {
    ncrs,
    stats,
    isLoading,
    filters,
    setFilters,
  } = useConstructionNcrs(companyId);

  return (
    <PageLayout
      title="Báo cáo Không phù hợp (NCR)"
      description="Quản lý các sai lệch kỹ thuật nghiêm trọng, yêu cầu khắc phục thực địa và hồ sơ pháp lý về chất lượng thi công."
      breadcrumbs={[{ label: 'Thi công' }, { label: 'NCR' }]}
      isLoading={isLoading}
      headerActions={
        <div className="flex gap-2">
          <Button variant="outline" icon="ri-file-chart-line">Báo cáo chất lượng</Button>
          <AuthButton
            module="construction"
            action="create"
            variant="primary"
            icon="ri-error-warning-line"
            onClick={() => navigate('./create')}
          >
            Lập NCR mới
          </AuthButton>
        </div>
      }
    >
      <div className="space-y-6">
        {/* KPI Board */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <KPICard
            title="NCR đang mở"
            value={stats?.openCount || 0}
            icon="ri-error-warning-line"
            variant="danger"
          />
          <KPICard
            title="Nghiêm trọng (High/Critical)"
            value={stats?.criticalCount || 0}
            icon="ri-alarm-warning-line"
            variant="warning"
          />
          <KPICard
            title="Ước tính chậm trễ"
            value={stats?.delayImpact || 0}
            icon="ri-timer-line"
            variant="info"
            suffix=" ngày"
          />
          <KPICard
            title="Thiệt hại dự kiến"
            value={stats?.costImpact.toLocaleString() || 0}
            icon="ri-money-dollar-circle-line"
            variant="primary"
            suffix=" VNĐ"
          />
        </div>

        <PageLayout.Toolbar>
          <div className="flex flex-col md:flex-row gap-4 w-full justify-between items-start md:items-center">
            <div className="flex flex-wrap gap-3 flex-1">
              <Search
                placeholder="Tìm mã NCR, tiêu đề sai lệch..."
                value={filters.search}
                onChange={(v) => setFilters({ ...filters, search: v })}
                className="w-full md:w-80"
              />
              <Filter
                options={[
                  {
                    id: 'status', label: 'Trạng thái', type: 'select', choices: [
                      { value: 'all', label: 'Tất cả trạng thái' },
                      ...Object.entries(statusLabels).map(([val, label]) => ({ value: val, label }))
                    ]
                  },
                  {
                    id: 'severity', label: 'Mức độ', type: 'select', choices: [
                      { value: 'all', label: 'Tất cả mức độ' },
                      { value: 'low', label: 'Thấp' },
                      { value: 'medium', label: 'Trung bình' },
                      { value: 'high', label: 'Cao' },
                      { value: 'critical', label: 'Nghiêm trọng' }
                    ]
                  }
                ]}
                onFilterChange={(k, v) => setFilters({ ...filters, [k]: v })}
              />
            </div>
          </div>
        </PageLayout.Toolbar>

        <Card className="overflow-hidden border-none shadow-sm">
          <Table
            columns={[
              { key: 'code', title: 'Mã NCR' },
              { key: 'title', title: 'Nội dung sai lệch & Khu vực' },
              { key: 'assignee', title: 'Trách nhiệm xử lý' },
              { key: 'severity', title: 'Mức độ', align: 'center' },
              { key: 'status', title: 'Trạng thái', align: 'center' },
            ]}
            data={ncrs}
            isLoading={isLoading}
            onRowClick={(item) => navigate(`./detail/${item.id}`)}
            renderCell={(item, key) => {
              switch (key) {
                case 'code':
                  return <span className="font-mono text-[11px] font-bold text-os-primary  tracking-wider">{item.ncr_code}</span>;
                case 'title':
                  return (
                    <div className="flex flex-col max-w-xs">
                      <span className="font-bold text-slate-900 line-clamp-1">{item.title}</span>
                      <div className="flex items-center gap-1.5 mt-0.5">
                        <span className="text-[10px] text-slate-400 font-bold ">{item.ncr_type}</span>
                        <span className="text-slate-300">•</span>
                        <span className="text-[10px] text-slate-400 font-medium italic">{item.zone?.name || 'Toàn dự án'}</span>
                      </div>
                    </div>
                  );
                case 'assignee':
                  return (
                    <div className="flex items-center gap-2">
                      <Avatar name={item.assignee?.full_name} size="xs" src={item.assignee?.avatar_url} />
                      <div className="flex flex-col">
                        <span className="text-xs font-bold text-slate-700 underline">{item.assignee?.full_name || 'Chưa gán'}</span>
                        <span className="text-[9px] text-slate-400 italic">{item.responsible_team?.name || '---'}</span>
                      </div>
                    </div>
                  );
                case 'severity':
                  return (
                    <Badge className={`${severityColors[item.severity_code]} border-transparent text-[10px] font-bold `}>
                      {item.severity_code}
                    </Badge>
                  );
                case 'status':
                  return (
                    <Badge className={`${statusColors[item.status_code]} border-transparent text-[10px] font-bold `}>
                      {statusLabels[item.status_code]}
                    </Badge>
                  );
                default:
                  return null;
              }
            }}
          />
        </Card>
      </div>
    </PageLayout>
  );
}
